/**
 * Hook to fetch and manage public API keys (read + write) for the current workspace.
 * Keys are encrypted in the database and always retrievable by workspace admins.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicApiKeyResponse } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';

type PublicApiKeysResponse = PublicApiKeyResponse;

/**
 * Hook to manage public API keys for the current workspace.
 * Returns read key (always present), write key (optional),
 * and mutation functions for regenerating/creating/deleting keys.
 */
export function usePublicApiKey() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  // Fetch both keys
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: queryKeys.security.publicApiKey,
    queryFn: async (): Promise<PublicApiKeysResponse> => {
      const res = await rpc.api.private['public-api-keys'].$get();

      if (!res.ok) {
        throw new Error('Failed to fetch public API keys');
      }

      const json = (await res.json()) as PublicApiKeysResponse;
      return json;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Regenerate read key
  const regenerateReadMutation = useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['public-api-keys'].regenerate.$post({
        json: { scope: 'read_only' },
      });
      if (!res.ok) throw new Error('Failed to regenerate read key');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.security.publicApiKey });
    },
  });

  // Regenerate write key
  const regenerateWriteMutation = useMutation({
    mutationFn: async () => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['public-api-keys'].regenerate.$post({
        json: { scope: 'read_write' },
      });
      if (!res.ok) throw new Error('Failed to regenerate write key');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.security.publicApiKey });
    },
  });

  // Delete write key
  const deleteWriteKeyMutation = useMutation({
    mutationFn: async (keyId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['public-api-keys'][':id'].$delete({
        param: { id: keyId },
      });
      if (!res.ok) throw new Error('Failed to delete write key');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.security.publicApiKey });
    },
  });

  return {
    // Key data
    readKey: data?.readKey?.key ?? null,
    writeKey: data?.writeKey?.key ?? null,
    writeKeyId: data?.writeKey?.id ?? null,

    // Backwards-compatible alias (read key)
    publicKey: data?.readKey?.key ?? null,

    // Loading state
    loading,
    error: error instanceof Error ? error : null,

    // Mutations
    regenerateReadKey: regenerateReadMutation.mutate,
    regeneratingRead: regenerateReadMutation.isPending,
    regenerateWriteKey: regenerateWriteMutation.mutate,
    regeneratingWrite: regenerateWriteMutation.isPending,
    createWriteKey: regenerateWriteMutation.mutate, // alias — regenerate creates if none exists
    creatingWriteKey: regenerateWriteMutation.isPending,
    deleteWriteKey: deleteWriteKeyMutation.mutate,
    deletingWriteKey: deleteWriteKeyMutation.isPending,

    // Legacy
    refetch: () => queryClient.invalidateQueries({ queryKey: queryKeys.security.publicApiKey }),
  };
}
